import React, { useState } from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, TextField, Button,
  Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Chip, FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Send, CheckCircle } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateAndScroll } from '../utils/navigation';
import { submitContactForm } from '../services/api';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SEO from '../components/common/SEO';

const HeroSection = styled(Box)({
  background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)',
  color: 'white',
  padding: '64px 0',
  textAlign: 'center',
});

const callerTypes = [
  { value: '', label: 'Select who you are...' },
  { value: 'affiliate', label: 'Affiliate / Partner' },
  { value: 'business', label: 'Business Owner' },
  { value: 'client', label: 'Client / Homeowner' },
  { value: 'college', label: 'College / University Representative' },
  { value: 'elderly', label: 'Elderly Assistance (free)' },
  { value: 'investor', label: 'Investor' },
  { value: 'legal', label: 'Legal' },
  { value: 'media', label: 'Media / Press' },
  { value: 'parent', label: 'Parent' },
  { value: 'solicitor', label: 'Solicitor / Vendor' },
  { value: 'student', label: 'Student / Prospective Intern' },
];

const reasonOptions = {
  affiliate: [
    'Affiliate or referral partnership inquiry',
    'Technology partnership',
    'Channel partnership',
    'Other',
  ],
  business: [
    'I want to book a business assessment',
    'Question about what the assessment covers',
    'Multi-location or recurring assessment inquiry',
    'Billing or payment question',
    'Other',
  ],
  client: [
    'I want to book an assessment',
    'Question about my upcoming assessment',
    'Question about my assessment report',
    'I need to reschedule',
    'Billing or payment question',
    'Other',
  ],
  college: [
    'We want to partner with CWC',
    'Question about our college relationship',
    'Student program coordination',
    'Marketing or co-branding opportunity',
    'Other',
  ],
  elderly: [
    'Is this phone call a scam?',
    'Is this text message a scam?',
    'Is this email a scam?',
    'Someone is pressuring me to send money or gift cards',
    'I think I have been scammed, what do I do?',
    'Request a free presentation at our senior facility',
    'General question about staying safe',
    'Other',
  ],
  investor: [
    'Investment opportunity inquiry',
    'Request for financial information',
    'Partnership or equity discussion',
    'Other',
  ],
  legal: [
    'Service agreement question',
    'Liability or insurance inquiry',
    'Data privacy or compliance question',
    'Other',
  ],
  media: [
    'Press inquiry or interview request',
    'Story or feature opportunity',
    'Request for company information',
    'Other',
  ],
  parent: [
    'Question about the home assessment service',
    'My child is interested in interning',
    'Safety and privacy concerns',
    'Other',
  ],
  solicitor: [
    'Product or service offering',
    'Other',
  ],
  student: [
    'I want to apply as an intern',
    'Question about intern requirements',
    'Question about my application status',
    'Question about intern pay and scheduling',
    'My college is not listed as a partner',
    'Other',
  ],
};

const validationSchema = Yup.object({
  caller_type: Yup.string().required('Please tell us who you are'),
  reason: Yup.string().required('Please select a reason for contacting us'),
  name: Yup.string().required('Your name is required'),
  email: Yup.string().email('Please enter a valid email address').required('Email is required'),
  phone: Yup.string()
    .matches(/^[\d\s()+\-]{7,20}$/, 'Please enter a valid phone number')
    .required('Phone number is required so we can schedule a call if needed'),
  organization: Yup.string(),
  message: Yup.string()
    .when('caller_type', {
      is: 'elderly',
      then: (schema) => schema.min(20, 'Please add a little more detail (at least 20 characters)'),
      otherwise: (schema) => schema.min(50, 'Please provide more detail (at least 50 characters)'),
    })
    .required('Please explain why you are reaching out'),
  honeypot: Yup.string().max(0), // spam trap
});


function ContactPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const formik = useFormik({
    initialValues: {
      caller_type: '', reason: '', name: '', email: '', phone: '',
      organization: '', message: '', honeypot: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Spam check: honeypot field should be empty
      if (values.honeypot) return;

      setSubmitting(true);
      setError(null);
      try {
        await submitContactForm(values);
        setSubmitted(true);
        resetForm();
      } catch (err) {
        setError(err?.error || err?.message || 'Something went wrong. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const currentReasons = formik.values.caller_type
    ? (reasonOptions[formik.values.caller_type] || ['Other'])
    : [];

  return (
    <Box>
      <SEO title="Contact Us" description="Get in touch with Cyber Wise Corp. We respond within 24 business hours." />
      {/* Hero */}
      <HeroSection>
        <Container maxWidth="lg">
          <Chip label="Put It In Writing" sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Contact Cyber Wise Corp
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95, maxWidth: 700, mx: 'auto' }}>
            Tell us who you are, why you're reaching out, and we'll get back to you within 24 business hours. If a call is needed, we'll schedule one.
          </Typography>
        </Container>
      </HeroSection>

      {/* Contact Form */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        {submitted ? (
          <Card sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
            <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>Message Received</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: '1.1rem' }}>
              Thank you for reaching out. We review every message personally and will respond within 24 business hours. If a phone call would be helpful, we'll schedule one at a time that works for you.
            </Typography>
            <Button variant="contained" onClick={() => setSubmitted(false)}
              sx={{ mt: 2, background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}>
              Send Another Message
            </Button>
          </Card>
        ) : (
          <Card sx={{ p: { xs: 3, md: 5 }, borderRadius: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              How Can We Help?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Please fill out the form below so we can route your inquiry to the right person. All fields marked with * are required.
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              {/* Honeypot - hidden from humans, visible to bots */}
              <TextField name="honeypot" value={formik.values.honeypot} onChange={formik.handleChange}
                sx={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                tabIndex={-1} autoComplete="off" />

              <Grid container spacing={3}>
                {/* Who are you */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={formik.touched.caller_type && Boolean(formik.errors.caller_type)}>
                    <InputLabel>I am a... *</InputLabel>
                    <Select name="caller_type" label="I am a... *"
                      value={formik.values.caller_type} onChange={(e) => {
                        formik.handleChange(e);
                        formik.setFieldValue('reason', ''); // reset reason when type changes
                      }} onBlur={formik.handleBlur}>
                      {callerTypes.filter(t => t.value).map(t => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.caller_type && formik.errors.caller_type && (
                      <FormHelperText>{formik.errors.caller_type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Reason - depends on caller type */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={!formik.values.caller_type}
                    error={formik.touched.reason && Boolean(formik.errors.reason)}>
                    <InputLabel>Reason for contacting us *</InputLabel>
                    <Select name="reason" label="Reason for contacting us *"
                      value={formik.values.reason} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                      {currentReasons.map(r => (
                        <MenuItem key={r} value={r}>{r}</MenuItem>
                      ))}
                    </Select>
                    {formik.touched.reason && formik.errors.reason && (
                      <FormHelperText>{formik.errors.reason}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Name */}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Full Name *" name="name"
                    value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name} />
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email Address *" name="email" type="email"
                    value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email} />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone Number *" name="phone"
                    value={formik.values.phone} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={(formik.touched.phone && formik.errors.phone) || 'We\'ll use this to schedule a call if needed'} />
                </Grid>

                {/* Organization */}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Organization / Company (optional)" name="organization"
                    value={formik.values.organization} onChange={formik.handleChange} />
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  <TextField fullWidth label="Please explain why you're reaching out *" name="message"
                    multiline rows={5}
                    value={formik.values.message} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    error={formik.touched.message && Boolean(formik.errors.message)}
                    helperText={(formik.touched.message && formik.errors.message) ||
                      `${formik.values.message.length}/50 characters minimum. Be specific so we can help you efficiently`} />
                </Grid>

                {/* Error */}
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                  </Grid>
                )}

                {/* Submit */}
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" size="large" disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                    sx={{ px: 5, py: 1.5, fontSize: '1rem', fontWeight: 600,
                      background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                      '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #6d28d9)' } }}>
                    {submitting ? 'Sending...' : 'Submit Message'}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    We respond to every message within 24 business hours. If a phone call is warranted, we'll reach out to schedule one at a convenient time.
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </Card>
        )}
      </Container>

      {/* Bottom note */}
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          Have a common question?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Check our FAQ page first. Most questions about assessments, pricing, and the intern program are answered there.
        </Typography>
        <Button variant="contained" size="large"
          onClick={() => navigateAndScroll(navigate, '/faq', location.pathname)}
          sx={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)', fontWeight: 600 }}>
          View FAQ
        </Button>
      </Container>
    </Box>
  );
}

export default ContactPage;
